import { useCallback, useRef, useState } from "react";

import { PARSED_ENV } from "@/constants/common";
import { useAppSelector } from "@/redux/hooks";

import {
	HelpChatRequest,
	HelpArticleSource,
} from "@/pages/Private/redux/helpCenter/helpCenter.schema";

import { SseFrameReader } from "./sseFrames";

/** What a finished turn leaves behind: the citations and the ids for threading and feedback. */
export interface StreamedTurn {
	sources: HelpArticleSource[];
	conversationId?: number;
	messageId?: number;
	canEscalate: boolean;
}

export interface HelpChatStreamHandlers {
	/** Called for every text fragment, in order — fragments split mid-word. */
	onDelta: (text: string) => void;
	onComplete: (turn: StreamedTurn) => void;
	/** The API's error code, or undefined when the failure carried none. */
	onError: (code?: string) => void;
}

/** Payload of the API's own trailing event, carrying the ids the assistant knows nothing about. */
interface SignalistTurnData {
	conversationId?: number;
	messageId?: number;
}

/**
 * Ask the help assistant and stream the answer back.
 *
 * Deliberately outside RTK Query, which buffers a whole response before it resolves — there is no
 * way to render a partial answer through it. The gap before the first token is the assistant
 * reading articles and runs to seconds with no heartbeat, so the thinking state is driven by the
 * request starting, not by the first fragment arriving.
 */
export const useHelpChatStream = () => {
	const token = useAppSelector((state) => state.auth.token);
	const [isStreaming, setIsStreaming] = useState(false);
	const abortRef = useRef<AbortController | null>(null);

	const send = useCallback(
		async (body: HelpChatRequest, handlers: HelpChatStreamHandlers): Promise<void> => {
			abortRef.current?.abort();

			const controller = new AbortController();

			abortRef.current = controller;
			setIsStreaming(true);

			try {
				const response = await fetch(`${PARSED_ENV.VITE_API_URL}/api/help-center/chat`, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Accept: "text/event-stream",
						...(token ? { Authorization: `Bearer ${token}` } : {}),
					},
					body: JSON.stringify(body),
					signal: controller.signal,
				});

				// Everything that can fail with a status code fails before the stream opens, and does
				// so as JSON. Once the headers are out the status is committed, so a later failure
				// arrives as an `error` event on this same 200 — see the reader below.
				if (!response.ok || !response.body) {
					handlers.onError(await readErrorCode(response));

					return;
				}

				await readStream(response.body, handlers);
			} catch (error) {
				if (!(error instanceof DOMException && error.name === "AbortError")) {
					handlers.onError();
				}
			} finally {
				setIsStreaming(false);
			}
		},
		[token]
	);

	return { send, isStreaming };
};

/** The API's error code from a pre-stream failure body, if it said anything usable. */
async function readErrorCode(response: Response): Promise<string | undefined> {
	try {
		const body = (await response.json()) as { message?: string };

		return typeof body.message === "string" ? body.message : undefined;
	} catch {
		return undefined;
	}
}

/** Read frames until the stream ends, reporting each one the chat knows how to use. */
async function readStream(body: ReadableStream, handlers: HelpChatStreamHandlers): Promise<void> {
	const reader = body.pipeThrough(new TextDecoderStream()).getReader();
	const frames = new SseFrameReader();

	let sources: HelpArticleSource[] = [];
	let ids: SignalistTurnData = {};
	let deflected = false;
	let failed = false;

	for (;;) {
		const { done, value } = await reader.read();

		if (done) {
			break;
		}

		for (const frame of [...frames.push(value)]) {
			const data = (frame.data ?? {}) as Record<string, unknown>;

			switch (frame.event) {
				case "delta":
					handlers.onDelta(String(data.text ?? ""));
					break;
				case "citations":
					sources = (data.citations as HelpArticleSource[]) ?? sources;
					break;
				case "done":
					deflected = Boolean(data.deflected);
					break;
				case "signalist.turn":
					ids = data as SignalistTurnData;
					break;
				case "error":
					failed = true;
					handlers.onError(typeof data.code === "string" ? data.code : undefined);
					break;
				default:
					// An event added upstream after this was written. Ignoring it is what keeps
					// additions from breaking this client.
					break;
			}
		}
	}

	if (!failed) {
		handlers.onComplete({ sources, canEscalate: deflected || sources.length === 0, ...ids });
	}
}
